import { forwardRef } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Slide,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { Close, OpenInNew } from '@mui/icons-material'
import { TransitionProps } from '@mui/material/transitions'
import { InsuranceProduct } from './insurance.types'

type InsuranceDetailDialogProps = Omit<DialogProps, 'onClose'> & {
  product: InsuranceProduct | null
  onClose: () => void
  onSimulate: (product: InsuranceProduct) => void
}

export function InsuranceDetailDialog({
  product,
  onClose,
  onSimulate,
  ...props
}: InsuranceDetailDialogProps) {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  if (!product) {
    return null
  }

  return (
    <Dialog
      {...props}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      TransitionComponent={isMobile ? Transition : undefined}
      aria-labelledby="insurance-detail-title"
      data-testid="insurance-detail-dialog"
    >
      <Box position="relative" flexShrink={0}>
        <Box
          component="img"
          src={product.image}
          alt={product.imageAlt}
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: 190, sm: 240 },
            objectFit: 'cover',
            objectPosition: product.imagePosition ?? 'center',
          }}
        />

        <IconButton
          aria-label="Fechar detalhes"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255, 255, 255, 0.85)',
            '&:hover': { bgcolor: 'common.white' },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogTitle id="insurance-detail-title" sx={{ pb: 1 }}>
        {product.title}
      </DialogTitle>

      <DialogContent>
        <Typography color="text.secondary" fontWeight={500} mb={1}>
          {product.subtitle}
        </Typography>
        <Typography mb={3}>{product.description}</Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6">{product.coverageTitle}</Typography>
            <InsuranceItemList items={product.coveredItems} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6">Por que vale a pena</Typography>
            <InsuranceItemList items={product.benefits} />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: 'primary.50',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <Box component="span" fontWeight={700}>
              Aviso:{' '}
            </Box>
            {product.disclaimer}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          fullWidth
          size="large"
          endIcon={product.simulateRoute ? undefined : <OpenInNew />}
          onClick={() => onSimulate(product)}
        >
          Simular
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function InsuranceItemList({ items }: { items: string[] }) {
  return (
    <List dense disablePadding sx={{ mt: 0.5 }}>
      {items.map((item) => (
        <ListItem
          key={item}
          disableGutters
          sx={{ alignItems: 'flex-start', py: 0.25 }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              mt: 1,
              mr: 1.25,
              flexShrink: 0,
            }}
          />
          <ListItemText
            primary={item}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      ))}
    </List>
  )
}

const Transition = forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
)
